import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Play, ExternalLink, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import QuickAnswer from '../components/QuickAnswer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/videos`);
        const data = await res.json();
        if (!cancelled) setVideos(data.videos || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = videos.reduce((acc, v) => {
    const key = v.category || 'Other';
    (acc[key] = acc[key] || []).push(v);
    return acc;
  }, {});

  return (
    <div className="bg-gradient-to-b from-[#e8f4fd] to-[#f0f8ff] min-h-screen py-20">
      <div className="max-w-5xl mx-auto px-6">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 text-center">
          Watch Levanta
        </h1>
        <p className="text-xl text-gray-600 text-center mb-10">
          Short videos explaining the Levanta system, the Four Worlds, and the principles of structured personal growth.
        </p>

        <QuickAnswer label="Why videos matter here">
          Watching is faster than reading when you want the big picture. These
          clips are built to give you, in under five minutes each, a clear
          sense of what Levanta does, how it works, and whether it&apos;s for you.
        </QuickAnswer>

        {loading && (
          <div className="text-center text-gray-500 py-16" data-testid="videos-loading">
            Loading videos…
          </div>
        )}

        {!loading && videos.length === 0 && (
          <Card className="bg-white rounded-3xl p-10 text-center">
            <p className="text-gray-700 text-lg">
              We&apos;re adding more videos — check back soon.
            </p>
          </Card>
        )}

        {Object.entries(grouped).map(([category, list]) => (
          <div key={category} className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{category}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {list.map((v) => (
                <Card
                  key={v.id}
                  data-testid={`video-card-${v.id}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  {activeId === v.id ? (
                    <div className="aspect-video bg-black">
                      <iframe
                        title={v.name}
                        src={`${v.embedUrl}?autoplay=1`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActiveId(v.id)}
                      data-testid={`video-play-${v.id}`}
                      className="relative block w-full aspect-video bg-gray-100 overflow-hidden group"
                      aria-label={`Play ${v.name}`}
                    >
                      {v.thumbnailUrl && (
                        <img
                          src={v.thumbnailUrl}
                          alt={v.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      )}
                      <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                        <span className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                          <Play className="w-7 h-7 text-blue-600 ml-1" fill="currentColor" />
                        </span>
                      </span>
                    </button>
                  )}
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{v.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{v.description}</p>
                    {v.watchUrl && (
                      <a
                        href={v.watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 text-sm hover:underline"
                      >
                        Watch on {v.provider || 'external'}
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}

        <div className="text-center mt-12">
          <p className="text-gray-700 mb-4">Subscribe for more on YouTube:</p>
          <a
            href="https://www.youtube.com/@Levanta_life"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="videos-youtube-subscribe"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-medium"
          >
            <Play className="w-4 h-4" fill="currentColor" />
            Subscribe on YouTube
          </a>
        </div>

        <div className="text-center mt-12">
          <Link to="/download-app">
            <Button
              data-testid="videos-cta-download"
              className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-5 text-lg rounded-xl shadow-md"
            >
              Download Levanta
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Videos;
